import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { appendedRows, VIEW_PAGE_SIZE } from '@/hooks/useFetchAllRows'
import { failureError, invalidError, notice } from '@/components/pbo/notice'
import { PboConfirm } from '@/components/pbo/pboDialog'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { downloadConsolOutboundsExcel } from '@/services/global/consolOutbound/apis'
import { apiErrMessage, hasOrderDateRange, isDirtyRow, mergeEditedRows, toDetailedReason, toSaveRow, toSearchFilter } from '@/services/global/consolOutbound/mapping'
import {
  useSyncConsolOutbounds,
  consolOutboundQueryKeys,
  useConfirmGlobalOutbounds,
  useGetConsolOutbounds,
  useSaveConsolOutbounds,
} from '@/services/global/consolOutbound/useConsolOutboundQueries'
import { MUSINSA_GLOBAL_SHIPPER_CODE } from '@/services/global/consolOutbound/types'
import type { ConfirmOutboundDoType, ConsolOutboundFilter as Filter, ConsolOutboundRow } from '@/services/global/consolOutbound/types'
import { isValidZipPassword, REASON_DETAIL_MAX_LENGTH, REASON_DETAIL_MIN_LENGTH, RESEND_DISABLED_REASON } from './components/consts'
import { fromSearchQuery, toSearchQuery } from './searchQuery'
import type { ConsolOutboundSearchQuery } from './searchQuery'
import GlobalConsolOutboundPageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'

/** 뷰가 onState 로 알리는 화면 상태(페이저·목록 검색어) */
type ViewState = Parameters<NonNullable<Wiring['onState']>>[0]

/** 안내 문구 — 슬롯 여러 곳에서 같은 문장을 쓴다(한 군데서 고치게 모아 둔다). */
const MESSAGES = {
  actionInProgress: '다른 작업이 진행 중입니다. 끝난 뒤 다시 시도해 주세요.',
  noOrderDate: '주문일자를 선택해 주세요.',
  noUser: '사용자 정보를 찾을 수 없습니다. 다시 로그인해 주세요.',
  noDirtyRows: '저장할 편집 내용이 없습니다. 편집한 행을 선택해 주세요.',
  noFilter: '조회한 뒤 내려받을 수 있습니다.',
  leaveDirty: '저장하지 않은 편집 내용이 있습니다. 저장은 페이지마다 해야 합니다. 이동할까요?',
  downloadValidationFailed: `사유코드를 고르고 상세사유를 ${REASON_DETAIL_MIN_LENGTH}~${REASON_DETAIL_MAX_LENGTH}글자로 적어 주세요.`,
  passwordInvalid: '압축 파일 비밀번호는 8자 이상이며 대문자·소문자·숫자·특수문자를 모두 포함해야 합니다.',
  syncSucceeded: '동기화를 요청했습니다. 1~2분 뒤 새로고침하면 반영됩니다.',
  syncFailed: '동기화 요청에 실패했습니다. 잠시 후 다시 시도하거나 문의 채널에 남겨 주세요.',
  confirmFailed: '요청에 실패했습니다.',
  saveFailed: '저장에 실패했습니다.',
  downloadFailed: '다운로드에 실패했습니다.',
} as const

/** yyyyMMdd → 'YYYY-MM-DD'(뷰 DateRangeField 값 모양). 8자리가 아니면 심지 않는다. */
const toDash = (v?: string): string | undefined => (v && /^\d{8}$/.test(v) ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}` : undefined)
const toRange = (from?: string, to?: string): [string, string] | undefined => {
  const a = toDash(from)
  const b = toDash(to)
  return a && b ? [a, b] : undefined
}

/** 화주는 무신사글로벌 한 곳이다(1.0 GL01 파리티) — 옵션 슬롯이 렌더마다 불리므로 모듈에서 한 번만 만든다. */
const SHIPPER_OPTIONS: Option[] = [{ value: MUSINSA_GLOBAL_SHIPPER_CODE, label: '무신사글로벌' }]

/** 되살린 조회 조건 → 조회영역 값(키 = filterKey). 없는 값은 얹지 않는다(기본값이 살아 있게). */
const restoredFilterValues = (filter: Filter | null): Record<string, unknown> => {
  if (!filter) return {}
  const ordDate = toRange(filter.orderDateFrom, filter.orderDateTo)
  const oubPdate = toRange(filter.outboundPlannedDateFrom, filter.outboundPlannedDateTo)
  return {
    ...(filter.shipperCode ? { SPR_NM: filter.shipperCode } : {}),
    ...(ordDate ? { ORD_DATE: ordDate } : {}),
    ...(oubPdate ? { OUB_PDATE: oubPdate } : {}),
    ...(filter.sourceOrderNumbers?.length ? { SPR_ORD_NO: filter.sourceOrderNumbers } : {}),
    ...(filter.inboundInvoiceNumber ? { INB_TRS_INVOICE: filter.inboundInvoiceNumber } : {}),
    ...(filter.globalInvoiceNumbers?.length ? { REF_NO2: filter.globalInvoiceNumbers } : {}),
    ...(filter.orderNumbers?.length ? { ORD_NO: filter.orderNumbers } : {}),
    ...(filter.serviceType ? { SVC_TYPE: filter.serviceType } : {}),
    ...(filter.orderStatus ? { ORD_STATUS: filter.orderStatus } : {}),
  }
}

/**
 * 합배송출고 2.0(GL15) — 1.0 GL01 을 pbo-core 로 재구현한 화면.
 *
 * 겉(조회영역·표·확인창·엑셀 사유 모달)은 컨덕터 뷰(`./handoff/page.generated`, 재핸드오프 때 통째로 갱신)가 그리고,
 * 이 파일은 옛 훅·API·세션/URL 유지·잠금을 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51).
 * 옛 페이지 컴포넌트와 표 컴포넌트(`components/ConsolOutbound*.tsx`)가 나눠 갖던 상태를 여기로 모았다 —
 * 확인창·선택·목록 검색·페이저는 뷰가 가지므로 그 짝은 지웠다.
 */
function GlobalConsolOutbound() {
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<ConsolOutboundSearchQuery>()
  // 마운트 스냅샷은 useRef 로 적는다 — useMemo 는 캐시라 React 가 언제든 버릴 수 있다.
  const restoredRef = useRef<ReturnType<typeof fromSearchQuery>>()
  restoredRef.current ??= fromSearchQuery(parsedQs)
  const restored = restoredRef.current

  /** URL 에 저장된 조건만 진입 조회에 쓴다. 신규 진입은 화면 기본값만 보여 주고 [조회]까지 요청하지 않는다. */
  const [filter, setFilter] = useState<Filter | null>(() => restored.filter)
  // 세션 사용자는 마운트 후 바뀌지 않는다 — 렌더마다 sessionStorage 를 다시 읽지 않는다.
  const userRef = useRef<string>()
  userRef.current ??= AuthStorage.user.get().email ?? ''
  const userId = userRef.current

  const { data: combos } = useGetCombos(['SVC_TYPE', 'ORD_STATUS', 'DOWNLOAD_CD'], { errorToast: true })
  const svcTypeOptions = useMemo(() => toSelectOptions(combos?.SVC_TYPE), [combos])
  const ordStatusOptions = useMemo(() => toSelectOptions(combos?.ORD_STATUS), [combos])
  const downloadReasonOptions = useMemo(() => toSelectOptions(combos?.DOWNLOAD_CD), [combos])

  // 전량을 구간으로 받는다(LOGISPRD-15271) — 페이지 이동은 받은 행 안에서라 다시 조회하지 않는다. 실패 안내는 훅이 전역 알럿으로 낸다.
  const { rows: fetchedRows, isFirstLoading, isFetchingRest, guardAction, refetch } = useGetConsolOutbounds(filter)
  const queryClient = useQueryClient()

  /** 편집 셀이 바꾼 행까지 반영한 표 행. 조회 결과가 새로 오면 원본으로 되돌린다. */
  const [rows, setRows] = useState<ConsolOutboundRow[]>([])
  /** 조회 직후 원본 — 저장 대상은 이것과 달라진 행만이다. */
  const originalRef = useRef<Map<string, ConsolOutboundRow>>(new Map())
  const prevFetchedRef = useRef<ConsolOutboundRow[]>([])
  useEffect(() => {
    // 초기화(filter=null)는 표를 비운다 — 조건을 지웠는데 이전 결과가 남으면 무엇의 결과인지 알 수 없다(1.0 도 같다).
    const next = filter ? fetchedRows : []
    // 남은 구간이 붙은 것이면 붙은 행만 덧붙인다 — 받는 동안 한 편집을 지우지 않는다.
    const tail = appendedRows(prevFetchedRef.current, next)
    if (tail) {
      setRows((prev) => [...prev, ...tail])
      tail.forEach((r) => originalRef.current.set(r._rowKey, { ...r }))
    } else {
      setRows(next)
      originalRef.current = new Map(next.map((r) => [r._rowKey, { ...r }]))
    }
    prevFetchedRef.current = next
  }, [fetchedRows, filter])

  const hasDirtyRows = useCallback(() => rows.some((r) => isDirtyRow(r, originalRef.current.get(r._rowKey))), [rows])
  /** 표에 실을 행 — key 를 _rowKey 로 준다(뷰 기본은 배열 인덱스라 재조회·페이지 이동 뒤 선택이 어긋난다). */
  const viewRows = useMemo(() => rows.map((r) => ({ ...r, key: r._rowKey })), [rows])
  /** 선택 행 중 실제로 편집된 행 */
  const dirtyTargets = useCallback(
    (ctx: ActionCtx): ConsolOutboundRow[] => {
      const picked = new Set(ctx.selected.map((r) => (typeof r._rowKey === 'string' ? r._rowKey : '')))
      return rows.filter((r) => picked.has(r._rowKey) && isDirtyRow(r, originalRef.current.get(r._rowKey)))
    },
    [rows],
  )

  /** 뷰는 마운트 직후에도 onState 를 한 번 부른다 — 복원값 그대로면 URL·세션을 다시 쓰지 않는다. */
  const lastViewRef = useRef({ page: restored.page, listQuery: restored.listQuery })
  /** 받는 중에 온 뷰 통지(페이지·목록 검색어) — 받기가 끝나면 처리한다(아래 flush effect, LOGISPRD-15349) */
  const pendingViewRef = useRef<ViewState | null>(null)

  const { mutateAsync: sync, isPending: isSyncing } = useSyncConsolOutbounds()
  const { mutateAsync: confirmOutbound, isPending: isConfirming } = useConfirmGlobalOutbounds()
  const { mutateAsync: save, isPending: isSaving } = useSaveConsolOutbounds()
  const [isDownloading, setDownloading] = useState(false)
  /**
   * 되돌리기 어려운 동작끼리 서로를 잠근다 — 동기화·출고확정·잔여출고확정·저장은 겹쳐 돌면 안 된다.
   * 출고확정은 Temporal 워크플로를 띄워 컷을 만들고 TMS·하류 WMS 까지 나가므로 취소할 방법이 없고,
   * 저장이 안 끝난 상태에서 확정이 들어가면 저장 전 값으로 컷이 만들어진다.
   */
  const isMutating = isSyncing || isConfirming || isSaving || isDownloading
  const actionLock = useRef(false)
  /** 다른 동작이 진행 중이면 알리고 true — 잡기(acquireAction)·확인창 열기 가드(canOpenAction)가 같은 조건·문구를 쓴다. */
  const blockIfBusy = useCallback((): boolean => {
    if (!actionLock.current && !isMutating) return false
    notice.failure(MESSAGES.actionInProgress) // 진행 중 대기 — 알럿
    return true
  }, [isMutating])
  const acquireAction = useCallback(() => {
    if (blockIfBusy()) return false
    actionLock.current = true
    return true
  }, [blockIfBusy])
  const releaseAction = useCallback(() => {
    actionLock.current = false
  }, [])
  /** 확인창을 열기 전 공통 가드(옛 beginExclusive 가 모달 앞에서 하던 검사). */
  const canOpenAction = useCallback((): boolean => !blockIfBusy(), [blockIfBusy])

  /** 마지막 조회 조건과 표 보기 상태를 메뉴 세션·URL 에 남긴다. reload() 를 빼면 탭 스트립이 옛 조건을 되살린다. */
  const persist = useCallback(
    (next: Filter, view?: { listQuery?: string; page?: number }) => {
      const query = toSearchQuery(next, view)
      MenuStorage.menuOnSession.setSearch(query)
      menuSession.reload()
      replaceQuery(query)
    },
    [menuSession, replaceQuery],
  )

  /** 출고확정·잔여출고확정 — 같은 엔드포인트를 doType 으로 나눠 쓴다. 응답이 raw DTO 라 성공 판정은 `result === true` 다. */
  const runConfirmOutbound = useCallback(
    async (doType: ConfirmOutboundDoType): Promise<string> => {
      // BE 가 userId 를 @NotBlank 로 받는다 — 세션이 비어 있으면 400 이 날 뿐이라 먼저 막는다.
      // 안내를 먼저 띄운다 — 확인창 onOk 는 reject 를 삼키므로(생성본), throw 만 하면 화면에 아무 말도 안 뜬다.
      if (!userId) {
        throw failureError(MESSAGES.noUser)
      }
      if (!acquireAction()) throw new Error(MESSAGES.actionInProgress)
      try {
        // 요청 실패(네트워크·4xx)와 업무 실패(result=false)는 문구 출처가 다르다 —
        //  앞은 BE meta.message(apiErrMessage), 뒤는 응답 본문의 message 다. 둘을 한 catch 로 묶으면
        //  업무 실패 문구가 기본 문구로 덮인다(1.0 이 100% 실패 팝업을 띄웠던 자리, LOGISPRD-10895).
        const res = await confirmOutbound({ shipperCode: MUSINSA_GLOBAL_SHIPPER_CODE, doType, userId }).catch((e: unknown) => {
          throw failureError(apiErrMessage(e, MESSAGES.confirmFailed))
        })
        if (!res.result) {
          throw failureError(res.message || MESSAGES.confirmFailed)
        }
        prevFetchedRef.current = [] // 재조회는 통째로 바꾼다(구조 공유로 '뒤에 붙음'으로 읽지 않게)
        await refetch()
        return res.message || '데이터 송신을 시작했습니다.'
      } finally {
        releaseAction()
      }
    },
    [confirmOutbound, userId, refetch, acquireAction, releaseAction],
  )

  // ── 뷰 배선(Wiring) — 옛 핸들러의 본문을 슬롯에 잇는다. 확인창·모달·선택·검색어·페이저는 뷰가 가진다. ──
  //  배선 객체는 매 렌더 새로 만든다(뷰가 렌더 첫머리에 props.wiring 을 모듈 홀더에 대입한다 — 문서 51).
  /** 페이지 이동 확인창이 떠 있는가 — 대기 중 재클릭을 막는다. */
  const leaveConfirmOpenRef = useRef(false)
  /** 페이지 이동·목록 검색어 확정을 받아 둔다(onState 본문 — 받는 중 미룬 통지도 여기로 온다). */
  const applyViewState = (s: ViewState) => {
    const next = s.pager.pager
    const listQuery = s.listq['table.q']
    const prev = lastViewRef.current
    if (prev.page === next.page && prev.listQuery === listQuery) return
    // 페이지를 옮기면 뷰가 목록 검색어를 '' 로 함께 보낸다. 페이지만 바뀌고 검색어가 남았으면 행이 줄어 뷰가 마지막 페이지로
    //  맞춘 것이다(LOGISPRD-15349) — 검색어를 지우지 않는다.
    lastViewRef.current = { page: next.page, listQuery }
    if (filter) persist(filter, lastViewRef.current)
  }
  // 받기가 끝나면 받는 중 미뤄 둔 통지를 처리한다 — 끝난 렌더에서 뷰가 다시 알렸으면 onState 가 먼저 비워 두어 여기선 할 일이 없다(자식 effect 가 먼저 돈다).
  useEffect(() => {
    if (isFirstLoading || isFetchingRest || pendingViewRef.current == null) return
    const pending = pendingViewRef.current
    pendingViewRef.current = null
    applyViewState(pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 받기가 끝나는 순간에만 돈다(applyViewState 는 매 렌더 새로 만든다)
  }, [isFirstLoading, isFetchingRest])

  const wiring: Wiring = {
    permission: usePermissionSlot('gl15'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { gl01Table: coreTableLayoutSlot('GL15_HEAD') },
    // total 없이 주면 뷰가 1만 건씩 페이지를 자르고 목록 검색을 전량에 건다(total 을 주면 서버 페이징 — 생성본 `ROWS_SERVERPAGED`)
    //  — 첫 구간·나머지 구간·재조회 중에는 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390). 받는 만큼 '전체 N'·페이지 수가 늘어나는 게 진행 표시다(LOGISPRD-15271).
    rows: { ROWS: () => ({ rows: viewRows as unknown as Row[], loading: isFirstLoading || isFetchingRest }) },
    actions: {
      /** [조회] — 조회영역 값(키 = filterKey)을 옛 필터 컴포넌트와 같은 변환으로 BE 조건으로 만든다. */
      search: async (filters) => {
        const parsed = toSearchFilter(filters)
        // 화주는 필수 + 단일 옵션이라 빈 값이 정상 상태가 아니다 — 그대로 보내면 BE 가 400 을 낸다.
        const next: Filter = { ...parsed, shipperCode: parsed.shipperCode || MUSINSA_GLOBAL_SHIPPER_CODE }
        if (!hasOrderDateRange(next)) {
          notice.invalid(MESSAGES.noOrderDate)
          return
        }
        persist(next)
        pendingViewRef.current = null // 옛 조건을 받는 중 미뤄 둔 통지가 새 조건에 섞이지 않게
        // 조건이 그대로여도 다시 읽는다(1.0 동작) — 같은 키는 캐시라 요청이 안 나가므로 그때만 refetch 한다.
        //  조건이 바뀌면 새 키가 스스로 받는다(둘 다 하면 새 조건 요청이 취소·재시작돼 한 번 더 나간다).
        if (filter && JSON.stringify(filter) === JSON.stringify(next)) {
          // 같은 조건 [조회]는 편집을 버린다(1.0) — 응답이 같으면 받은 행 참조가 그대로라 effect 가 안 돌아, 누르는 자리에서 받은 행으로 되돌린다(#1017 리뷰).
          setRows(fetchedRows)
          originalRef.current = new Map(fetchedRows.map((r) => [r._rowKey, { ...r }]))
          prevFetchedRef.current = fetchedRows
          await refetch()
          return
        }
        // 조건이 바뀌면(초기화 뒤 포함) 그 키의 지난 캐시(1분)를 지우고 옮긴다 — 안 지우면 이전 조건으로 돌아왔을 때 다시 읽지 않는다(ST09 와 같다).
        queryClient.removeQueries({ queryKey: consolOutboundQueryKeys.list(next), exact: true })
        setFilter(next)
      },
      /** 편집 셀 — 표는 보이는 행만 돌려준다. 그대로 쓰면 목록 검색으로 가려진 행이 사라진다. */
      ROWS_rowsChange: (changed) => {
        setRows((prev) => mergeEditedRows(prev, changed as unknown as ConsolOutboundRow[]))
      },
      /** [동기화] 확인 — 화주·기간 없이 즉시 동기화를 1회 요청한다. 반영은 1~2분 뒤라 목록을 다시 읽지 않는다. */
      gl01CollectConfirm_ok: async () => {
        if (!acquireAction()) throw new Error(MESSAGES.actionInProgress)
        try {
          const res = await sync()
          return res.meta?.message || MESSAGES.syncSucceeded
        } catch (e) {
          throw failureError(apiErrMessage(e, MESSAGES.syncFailed))
        } finally {
          releaseAction()
        }
      },
      /** [출고확정] 확인 */
      gl01OutboundConfirmModal_ok: () => runConfirmOutbound('RELEASE'),
      /** [잔여출고확정] 확인 — 1일 1회, 티몰 주문 제외(문구는 뷰가 그린다). */
      gl01RemainConfirmModal_ok: () => runConfirmOutbound('REMAINING_RELEASE'),
      /** [저장] 확인 — 선택한 행 중 실제로 편집된 행만 보낸다. */
      gl01SaveConfirm_ok: async (ctx) => {
        const targets = dirtyTargets(ctx)
        if (targets.length === 0) {
          throw invalidError(MESSAGES.noDirtyRows)
        }
        if (!acquireAction()) throw new Error(MESSAGES.actionInProgress)
        try {
          const res = await save({
            creates: [],
            updates: targets.map((row) => toSaveRow(row, originalRef.current.get(row._rowKey))),
          })
          prevFetchedRef.current = [] // 재조회는 통째로 바꾼다(구조 공유로 '뒤에 붙음'으로 읽지 않게)
          await refetch()
          return `${res.data?.updatedCount ?? targets.length}개 저장 완료`
        } catch (e) {
          throw failureError(apiErrMessage(e, MESSAGES.saveFailed))
        } finally {
          releaseAction()
        }
      },
      /**
       * [재전송] 확인 — 2.0 API 부재.
       * TODO(api): 1.0 SND_DO_TO_WH 는 moms-be 미구현(LOGISPRD-9605 보류). 1.0 /api/internal/data/send 우회는 금지.
       * 버튼은 opens 가드에서 막으므로 여기까지 오지 않는다 — 계약상 슬롯은 채워 둔다.
       */
      gl01ResendConfirm_ok: () => {
        throw invalidError(RESEND_DISABLED_REASON)
      },
      /** 엑셀 사유 모달 [확인] — 사유코드·상세사유·압축 비밀번호로 마지막 조회 조건을 내려받는다. */
      gl01DownloadModal_ok: async (ctx) => {
        if (!filter) {
          throw invalidError(MESSAGES.noFilter)
        }
        const reason = typeof ctx.form?.reason === 'string' ? ctx.form.reason : ''
        const detail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
        // 옛 모달은 [확인] 을 잠가 이 선을 지켰다 — 생성본 모달에는 그 잠금이 없다(컨덕터 후속).
        //  그래서 형식까지 여기서 본다. 규칙은 components/consts.ts 한 곳에만 둔다.
        const password = typeof ctx.form?.password === 'string' ? ctx.form.password : ''
        if (!reason || detail.length < REASON_DETAIL_MIN_LENGTH || detail.length > REASON_DETAIL_MAX_LENGTH) {
          throw invalidError(MESSAGES.downloadValidationFailed)
        }
        // 파일 비밀번호 — BE 가 없으면 400 을 낸다. 형식이 어긋나면 받은 zip 을 열 수 없다.
        if (!isValidZipPassword(password)) {
          throw invalidError(MESSAGES.passwordInvalid)
        }
        if (!acquireAction()) throw new Error(MESSAGES.actionInProgress)
        setDownloading(true)
        try {
          await downloadConsolOutboundsExcel({ ...filter, password, detailedReason: toDetailedReason(reason, detail) })
        } catch (e) {
          throw failureError(apiErrMessage(e, MESSAGES.downloadFailed))
        } finally {
          setDownloading(false)
          releaseAction()
        }
      },
    },
    options: {
      GL01SHIPPER_OPTIONS: () => SHIPPER_OPTIONS,
      GL01SVCTYPE_OPTIONS: () => svcTypeOptions,
      GL01ORDSTATUS_OPTIONS: () => ordStatusOptions,
      REASONRADIO_OPTIONS: () => downloadReasonOptions,
    },
    // 조회 기본값(화주 고정 + 오늘) · 진입 초기값(URL 복원 조건) — [초기화] 는 기본값으로 돌아간다.
    filterDefaults: { gl01FilterCard: (demo) => ({ ...demo, SPR_NM: MUSINSA_GLOBAL_SHIPPER_CODE }) },
    filterInitial: { gl01FilterCard: (defaults) => ({ ...defaults, ...restoredFilterValues(restored.filter) }) },
    useBusy: () => isMutating,
    opens: {
      gl01CollectButton: () => canOpenAction(),
      // 받는 중·일부 실패면 확인창을 열지 않는다(guardAction 이 안내한다) — 저장 대상·확정 판단이 덜 받은 목록 위에서 이뤄지지 않게.
      gl01OutboundConfirmButton: () => canOpenAction() && guardAction(),
      gl01RemainConfirmButton: () => canOpenAction() && guardAction(),
      gl01SaveButton: (ctx) => {
        if (!canOpenAction() || !guardAction()) return false
        if (dirtyTargets(ctx).length === 0) {
          notice.invalid(MESSAGES.noDirtyRows)
          return false
        }
        return true
      },
      // 스펙에 있는 버튼이라 숨기지 않는다 — 2.0 API 가 생기면 위 슬롯에 배선한다.
      gl01ResendButton: () => {
        notice.invalid(RESEND_DISABLED_REASON)
        return false
      },
      gl01ExcelButton: () => {
        if (!canOpenAction()) return false
        if (!filter) {
          notice.invalid(MESSAGES.noFilter)
          return false
        }
        return true
      },
    },
    stateInitial: { pager: { pager: { page: restored.page, size: VIEW_PAGE_SIZE } }, listq: { 'table.q': restored.listQuery } },
    /**
     * 페이지를 옮기기 **전** — 미저장 편집이 있으면 물어보고, 취소하면 옮기지 않는다.
     * 옛 구현(지운 ConsolOutboundTable `onPage`)이 확인창으로 지키던 선이다.
     * onState 는 이미 바뀐 뒤 통지라 되돌릴 수 없다 — 막는 자리는 여기다(codegen 불변식 ⑪).
     * 신규 디자인 확인창(PboConfirm)으로 묻는다 — 생성본 페이저가 Promise 답을 기다렸다 옮긴다(컨덕터 codegen #702, 기타출고와 같은 모양).
     */
    canChangePage: () => {
      if (!hasDirtyRows()) return true
      // 확인창이 떠 있는 동안 페이저를 또 누르면 무시한다 — 확인창이 겹치고 두 답이 모두 이동(go)을 돌리지 않게(#899 리뷰 sookeunwoo).
      if (leaveConfirmOpenRef.current) return false
      leaveConfirmOpenRef.current = true
      return new Promise<boolean>((resolve) => {
        const answer = (ok: boolean) => {
          leaveConfirmOpenRef.current = false
          resolve(ok)
        }
        PboConfirm.show(MESSAGES.leaveDirty, { onConfirm: () => answer(true), onClose: () => answer(false), okText: '이동' })
      })
    },
    /** 페이지 이동·목록 검색어 확정 — 옛 onViewChange. 페이지 이동은 받은 행 안에서라 다시 조회하지 않고 URL 에만 남긴다. */
    onState: (s) => {
      // 받는 중에는 저장을 미룬다 — 덜 온 행으로 뷰가 마지막 페이지로 줄여 알린 값이 복원 페이지를 덮는다(LOGISPRD-15349).
      //  받는 중 사용자가 옮긴 페이지·검색어도 여기로 오므로 버리지 않고 마지막 통지를 두었다가 받기가 끝나면 처리한다.
      if (isFirstLoading || isFetchingRest) {
        pendingViewRef.current = s
        return
      }
      pendingViewRef.current = null
      applyViewState(s)
    },
    /** [초기화] — 저장된 조건까지 비워 다음 진입이 기본값(오늘)으로 열리게 한다. */
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
      setFilter(null)
      lastViewRef.current = { page: 1, listQuery: '' }
      pendingViewRef.current = null
    },
  }

  return <GlobalConsolOutboundPageView wiring={wiring} />
}

export default GlobalConsolOutbound
